// ./src/components/features/EventGuests/LinkedAdultsSection.tsx
//
// "This invitation also goes to" — the OTHER adults of an invited family, on the guest's own drawer (ROADMAP A2b #62
// P6(c) Phase B). A family is ONE guest (this row, the group size); each adult listed here has their own link to it, is
// sent the invitation and reminders with it, and replies to the same family. The host can stop sending one of them.
//
// ⚠ Renders NOTHING for an invitation with no linked adults (most guests) — but a FAILED read is said, never shown as
//   "nobody": the host would conclude Lynn is not getting the invitation when she is.
// ⚠ Removing is per row with its own spinner and toasts only on failure (the row leaving the list is the confirmation).
// ⓘ Why a new component (the reuse gate): nothing existing lists another table's rows inside the guest drawer; the pieces
//   it is built from are the generics (`ConfirmModal`, `AppIcon`), and the read is its own service.

import React, { useEffect, useState } from "react";
import { ActionIcon, Box, Group, Paper, Stack, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { formatPhone } from "@em/utils";

import { useToast } from "@/hooks";
import { EventGuestInvitee, EventGuestInviteeService } from "@/utils/api";
import { AppIcon, ConfirmModal } from "@/components/generic";
import { refusalReason } from "@/utils";

export interface LinkedAdultsSectionProps {
  eventId: string;
  /** The family's invitation (its lead guest). */
  eventGuestId: string;
}

const nameOf = (l: EventGuestInvitee): string =>
  [l.person?.firstName, l.person?.lastName].filter(Boolean).join(" ").trim() || l.person?.email || l.person?.phone || "—";

export const LinkedAdultsSection: React.FC<LinkedAdultsSectionProps> = ({ eventId, eventGuestId }) => {
  const { t } = useTranslation(["events", "common"]);
  const toast = useToast();
  const [links, setLinks] = useState<EventGuestInvitee[]>([]);
  const [failed, setFailed] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [pending, setPending] = useState<EventGuestInvitee | null>(null);

  useEffect(() => {
    let alive = true;

    // ⚠ A different invitation is a different first read — never show the previous guest's adults while this one loads.
    setLinks([]);
    setFailed(false);

    EventGuestInviteeService.listForGuests(eventId, [eventGuestId]).then((res) => {
      if (!alive) {
        return;
      }

      setFailed(!res.didSucceed());
      setLinks(res.didSucceed() && Array.isArray(res.data) ? (res.data as EventGuestInvitee[]) : []);
    });

    return () => {
      alive = false;
    };
  }, [eventId, eventGuestId]);

  const remove = async () => {
    const link = pending;

    if (!link?.id) {
      return;
    }

    setRemovingId(link.id);

    const res = await EventGuestInviteeService.remove(link.id);

    setRemovingId(null);

    if (!res.didSucceed()) {
      toast.error(refusalReason(res));

      return;
    }

    setPending(null);
    setLinks((prev) => prev.filter((l) => l.id !== link.id));
  };

  if (failed) {
    return (
      <Text size="md" c="orange.8">
        {t("events:guests.linked.loadFailed", { defaultValue: "Could not check who else receives this invitation." })}
      </Text>
    );
  }

  if (links.length === 0) {
    return null;
  }

  return (
    <Stack gap="xs">
      <Box>
        <Text fw={600} size="md">
          {t("events:guests.linked.title", { defaultValue: "This invitation also goes to" })}
        </Text>
        <Text size="md" c="dimmed">
          {t("events:guests.linked.hint", {
            defaultValue: "Each has their own link to this one invitation: they get it whenever you send this invitation or its reminders, reply for the same family, and are not counted again.",
          })}
        </Text>
      </Box>
      {links.map((l) => {
        const contact = l.person?.email || (l.person?.phone ? formatPhone(l.person.phone) : null);
        const status = l.lastVisitedAt
          ? t("events:guests.linked.opened", { defaultValue: "opened their link" })
          : l.lastSentAt
            ? t("events:guests.linked.sent", { defaultValue: "invitation sent" })
            : t("events:guests.linked.notSent", { defaultValue: "not sent yet" });

        return (
          <Paper key={l.id} withBorder radius="md" p="xs">
            <Group justify="space-between" wrap="nowrap" gap="sm">
              <Box style={{ minWidth: 0 }}>
                <Text size="md" fw={600} lineClamp={1}>
                  {nameOf(l)}
                </Text>
                <Text size="md" c="dimmed" lineClamp={1}>
                  {[contact, status].filter(Boolean).join(" · ")}
                </Text>
              </Box>
              {(l.rights ?? []).includes("remove") ? (
                <ActionIcon
                  variant="subtle"
                  color="red"
                  size="lg"
                  loading={removingId === l.id}
                  disabled={!!removingId && removingId !== l.id}
                  onClick={() => setPending(l)}
                  aria-label={t("events:guests.linked.remove", { defaultValue: "Stop sending to {{name}}", name: nameOf(l) })}
                >
                  <AppIcon iconName="X" size={18} />
                </ActionIcon>
              ) : null}
            </Group>
          </Paper>
        );
      })}

      <ConfirmModal
        opened={!!pending}
        onClose={() => setPending(null)}
        onConfirm={() => void remove()}
        type="danger"
        confirmInProgress={!!removingId}
        title={t("events:guests.linked.removeTitle", { defaultValue: "Stop sending this invitation to {{name}}?", name: pending ? nameOf(pending) : "" })}
        message={t("events:guests.linked.removeMessage", {
          defaultValue: "Their link stops working and they get no more messages about it. The invitation itself, and everyone's replies, stay as they are.",
        })}
        confirmLabel={t("events:guests.linked.removeConfirm", { defaultValue: "Stop sending" })}
      />
    </Stack>
  );
};
